import { Injectable, inject } from '@angular/core';
import { environment } from '../../../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { EmployeePositionId } from '../../../classes/models/employee/Employee';
import { DayOfWeek } from '../../../classes/enums/DayOfWeek';
import { PreferredTimeOff } from '../../../classes/models/employee/PreferredTimeOff';
import {
  AddEmployeeWeeklyPreferredTimeOffRequest,
  EditEmployeeWeeklyPreferredTimeOffRequest,
  EmployeePreferredTimeOffRequest
} from '../../../interfaces/request/EmployeeRequests';

/**
 * This service communicates with backend to handle employee weekly preferred time off data management.
 */
@Injectable({
  providedIn: 'root'
})
export class EmployeeWeeklyPreferredTimeOffService {
  private http = inject(HttpClient);

  url = environment.apiUrl + '/employees';

  /**
   * Adds a new preferred time off to the employee.
   * @param id identifiers of employee and position.
   * @param startDate the date starting from which new preferred times off will take effect.
   * @param preferredTimesOff new preferred times off to add.
   */
  addPreferredTimeOff(id: EmployeePositionId, startDate: string, preferredTimesOff: Map<DayOfWeek, PreferredTimeOff>) {
    const url = `${this.url}/${id.parseEmployeeId()}/positions/${id.parsePositionId()}/weekly-preferred-times-off`;
    const requestBody: AddEmployeeWeeklyPreferredTimeOffRequest = {
      date: startDate,
      monday: this.asPreferredTimeOffRequest(preferredTimesOff.get(DayOfWeek.MONDAY)),
      tuesday: this.asPreferredTimeOffRequest(preferredTimesOff.get(DayOfWeek.TUESDAY)),
      wednesday: this.asPreferredTimeOffRequest(preferredTimesOff.get(DayOfWeek.WEDNESDAY)),
      thursday: this.asPreferredTimeOffRequest(preferredTimesOff.get(DayOfWeek.THURSDAY)),
      friday: this.asPreferredTimeOffRequest(preferredTimesOff.get(DayOfWeek.FRIDAY)),
      saturday: this.asPreferredTimeOffRequest(preferredTimesOff.get(DayOfWeek.SATURDAY)),
      sunday: this.asPreferredTimeOffRequest(preferredTimesOff.get(DayOfWeek.SUNDAY))
    };
    return this.http.post(url, requestBody);
  }

  /**
   * Edits preferred time off for the given employee.
   * @param id identifiers of employee and position.
   * @param startDate the date starting from which preferred times off will be edited.
   * @param preferredTimesOff preferred times off to be edited.
   */
  editPreferredTimeOff(id: EmployeePositionId, startDate: string, preferredTimesOff: Map<DayOfWeek, PreferredTimeOff>) {
    const url = `${this.url}/${id.parseEmployeeId()}/positions/${id.parsePositionId()}/weekly-preferred-times-off/${startDate}`;
    const requestBody: EditEmployeeWeeklyPreferredTimeOffRequest = {
      monday: this.asPreferredTimeOffRequest(preferredTimesOff.get(DayOfWeek.MONDAY)),
      tuesday: this.asPreferredTimeOffRequest(preferredTimesOff.get(DayOfWeek.TUESDAY)),
      wednesday: this.asPreferredTimeOffRequest(preferredTimesOff.get(DayOfWeek.WEDNESDAY)),
      thursday: this.asPreferredTimeOffRequest(preferredTimesOff.get(DayOfWeek.THURSDAY)),
      friday: this.asPreferredTimeOffRequest(preferredTimesOff.get(DayOfWeek.FRIDAY)),
      saturday: this.asPreferredTimeOffRequest(preferredTimesOff.get(DayOfWeek.SATURDAY)),
      sunday: this.asPreferredTimeOffRequest(preferredTimesOff.get(DayOfWeek.SUNDAY))
    };
    return this.http.put(url, requestBody);
  }

  private asPreferredTimeOffRequest(preferredTimeOff: PreferredTimeOff | undefined): EmployeePreferredTimeOffRequest | null {
    if (!preferredTimeOff) {
      return null;
    }
    return { startTime: preferredTimeOff.startTime, endTime: preferredTimeOff.endTime, reason: preferredTimeOff.reason };
  }
}
